<template>
    <v-container class="fill-height pa-4" fluid>
        <v-row justify="center" align="center">
            <v-col cols="12" sm="9" md="6" lg="4" xl="3">
                <v-card class="pa-6" rounded="lg" elevation="3">
                    <div class="text-overline grey--text mb-3">DG Ventures</div>

                    <!-- loading -->
                    <div v-if="state === 'loading'" class="text-center py-10">
                        <v-progress-circular indeterminate color="primary" size="36" />
                    </div>

                    <!-- link expired / invalid -->
                    <template v-else-if="state === 'expired'">
                        <div class="text-h4 mb-2">⏰</div>
                        <div class="text-h6 font-weight-bold mb-1">This link has expired</div>
                        <p class="text-body-2 grey--text text--darken-1 mb-5">
                            Approve / Decline links are valid for 24 hours. You can still review
                            and act on this request from the dashboard.
                        </p>
                        <v-btn href="/leaveApproval" color="grey lighten-3" depressed block class="text-none">
                            Open the dashboard
                        </v-btn>
                    </template>

                    <!-- generic failure -->
                    <template v-else-if="state === 'error'">
                        <div class="text-h4 mb-2">⚠️</div>
                        <div class="text-h6 font-weight-bold mb-1">Something went wrong</div>
                        <p class="text-body-2 grey--text text--darken-1 mb-5">
                            {{ errorMessage || 'We couldn’t load this request. Please use the dashboard.' }}
                        </p>
                        <v-btn href="/leaveApproval" color="grey lighten-3" depressed block class="text-none">
                            Open the dashboard
                        </v-btn>
                    </template>

                    <!-- already decided -->
                    <template v-else-if="state === 'decided'">
                        <div class="text-h4 mb-2">☑️</div>
                        <div class="text-h6 font-weight-bold mb-1">
                            Already {{ data.status === 'approved' ? 'approved' : 'declined' }}
                        </div>
                        <p class="text-body-2 grey--text text--darken-1 mb-5">
                            {{ data.request.employee }}’s {{ data.request.type }} request was already
                            {{ data.status === 'approved' ? 'approved' : 'declined' }}<template
                                v-if="data.decided_by"> by {{ data.decided_by }}</template>.
                            No further action is needed.
                        </p>
                        <v-btn href="/leaveApproval" color="grey lighten-3" depressed block class="text-none">
                            Open the dashboard
                        </v-btn>
                    </template>

                    <!-- decision recorded -->
                    <template v-else-if="state === 'done'">
                        <div class="text-h4 mb-2">{{ result === 'approved' ? '✅' : '❌' }}</div>
                        <div class="text-h6 font-weight-bold mb-1">
                            {{ result === 'approved' ? 'Approved' : 'Declined' }}
                        </div>
                        <p class="text-body-2 grey--text text--darken-1 mb-5">
                            {{ data.request.employee }}’s {{ data.request.type }} for
                            {{ data.request.dates_label }} has been {{ result }}. They’ve been
                            notified by email.
                        </p>
                        <v-btn href="/leaveApproval" color="grey lighten-3" depressed block class="text-none">
                            Open the dashboard
                        </v-btn>
                    </template>

                    <!-- confirm -->
                    <template v-else-if="state === 'confirm'">
                        <div class="text-h6 font-weight-bold mb-1">
                            {{ isApprove ? 'Approve' : 'Decline' }} this request?
                        </div>
                        <p class="text-body-2 grey--text text--darken-1 mb-4">
                            {{ data.request.employee }}’s {{ data.request.type }} request
                        </p>

                        <v-simple-table dense class="lac-table mb-3">
                            <tbody>
                                <tr>
                                    <td class="grey--text">Employee</td>
                                    <td class="text-right font-weight-medium">{{ data.request.employee }}</td>
                                </tr>
                                <tr>
                                    <td class="grey--text">Type</td>
                                    <td class="text-right font-weight-medium">{{ data.request.type }}</td>
                                </tr>
                                <tr>
                                    <td class="grey--text">Dates</td>
                                    <td class="text-right font-weight-medium">{{ data.request.dates_label }}</td>
                                </tr>
                                <tr>
                                    <td class="grey--text">Duration</td>
                                    <td class="text-right font-weight-medium">{{ data.request.duration }}</td>
                                </tr>
                            </tbody>
                        </v-simple-table>

                        <v-alert v-if="data.request.note" dense text color="grey darken-1" class="text-body-2 mb-3">
                            {{ data.request.note }}
                        </v-alert>

                        <div v-if="data.coverage && data.coverage.length" class="text-body-2 mb-4">
                            <div class="grey--text mb-1">Also off then</div>
                            <div v-for="(c, i) in data.coverage" :key="i" class="grey--text text--darken-2">
                                • {{ c.name }} — {{ c.type }} ({{ c.dates }})
                            </div>
                        </div>

                        <v-textarea v-model="note" outlined dense rows="2" hide-details class="mb-4"
                            :label="`Note for ${firstName} (optional)`" placeholder="Add a short message…" />

                        <v-btn :color="isApprove ? 'green darken-2' : 'red darken-2'" dark depressed block
                            class="text-none" :loading="submitting" @click="submit">
                            Confirm {{ isApprove ? 'approval' : 'decline' }}
                        </v-btn>

                        <p v-if="data.request.requested_at" class="text-caption grey--text mt-4 mb-0">
                            Requested {{ requestedAt }}
                        </p>
                    </template>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import dayjs from 'dayjs';

export default {
    name: 'LeaveActionComponent',
    data() {
        return {
            state: 'loading', // loading | confirm | decided | done | expired | error
            data: null,
            note: '',
            submitting: false,
            result: null,
            errorMessage: '',
        };
    },
    computed: {
        id() {
            return this.$route.params.id;
        },
        decision() {
            return this.$route.params.decision;
        },
        isApprove() {
            return this.decision === 'approve';
        },
        // The signed capability token, forwarded verbatim to the API.
        query() {
            return window.location.search;
        },
        endpoint() {
            return `/api/leave/action/${this.id}/${this.decision}`;
        },
        firstName() {
            const name = (this.data && this.data.request && this.data.request.employee) || '';
            return name.split(' ')[0] || 'the employee';
        },
        requestedAt() {
            const at = this.data && this.data.request && this.data.request.requested_at;
            return at ? dayjs(at).format('D MMM YYYY, h:mm A') : '';
        },
    },
    async mounted() {
        if (!['approve', 'decline'].includes(this.decision)) {
            this.state = 'error';
            return;
        }

        const res = await this.$axios.get(this.endpoint + this.query);
        this.applyResponse(res, (data) => {
            this.data = data;
            this.state = data.status === 'pending' ? 'confirm' : 'decided';
        });
    },
    methods: {
        applyResponse(res, onOk) {
            if (res && res.status === 200 && res.data) {
                onOk(res.data);
                return;
            }
            if (res && res.status === 403 && res.data && res.data.error === 'expired') {
                this.state = 'expired';
                return;
            }
            this.errorMessage = (res && res.data && res.data.message) || '';
            this.state = 'error';
        },
        async submit() {
            this.submitting = true;
            const res = await this.$axios.post(this.endpoint + this.query, { note: this.note || null });
            this.submitting = false;

            this.applyResponse(res, (data) => {
                this.data = data;
                if (data.already) {
                    this.state = 'decided';
                } else {
                    this.result = data.status;
                    this.state = 'done';
                }
            });
        },
    },
};
</script>

<style scoped>
.lac-table >>> td {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
    height: 38px;
}
</style>
